import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n/LanguageContext';
import { loadEvents } from '../../lib/events';
import './MainHeroSection.css';

const SOCIALS = [
  { label: 'Spotify',     href: 'https://open.spotify.com/artist/1QhaqxeqF9sipS2gwbEKpu', icon: 'fa-brands fa-spotify' },
  { label: 'YouTube',     href: 'https://www.youtube.com/channel/UCkD89TEFWsTZluY6tk84yqg', icon: 'fa-brands fa-youtube' },
  { label: 'Instagram',   href: 'https://www.instagram.com/ckan98', icon: 'fa-brands fa-instagram' },
  { label: 'Apple Music', href: 'https://music.apple.com/us/artist/c-kan/395579929', icon: 'fa-brands fa-apple' },
  { label: 'X',           href: 'https://x.com/ckan98', icon: 'fa-brands fa-x-twitter' },
];

function HeroSection() {
  const { t, lang } = useT();
  const [nextShow, setNextShow] = useState(null);

  useEffect(() => {
    let mounted = true;
    loadEvents().then(data => {
      if (!mounted || !data) return;
      const today = new Date().toISOString().slice(0, 10);
      const items = (data.features || [])
        .map(f => f.properties)
        .filter(p => {
          const [m, d, y] = String(p.date).split('/');
          if (!y) return false;
          return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` >= today;
        })
        .sort((a, b) => {
          const parse = s => {
            const [m, d, y] = String(s.date).split('/');
            return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
          };
          return parse(a).localeCompare(parse(b));
        });
      if (items.length) setNextShow(items[0]);
    });
    return () => { mounted = false; };
  }, []);

  const nextDate = nextShow && (() => {
    const [m, d, y] = String(nextShow.date).split('/');
    const monthsEs = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'];
    const monthsEn = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
    const months = lang === 'en' ? monthsEn : monthsEs;
    return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
  })();

  const subParts = t('hero.titleSubParts');
  const safeSubParts = Array.isArray(subParts) && subParts.length === 4
    ? subParts
    : ['the', 'voice', 'of a', 'generation'];

  return (
    <section className="hero">
      {/* Media layer */}
      <div className="hero__media">
        <img
          className="hero__video"
          src={process.env.PUBLIC_URL + '/assets/ckan-banner.jpg'}
          alt="C-Kan"
        />
        <div className="hero__vignette" />
        <div className="hero__scanlines" />
      </div>

      {/* Top status bar */}
      <div className="hero__topbar">
        <div className="hero__status">
          <span className="hero__dot" />
          {t('hero.statusOnTour')}
        </div>
        <div className="hero__location">
          <i className="fa-solid fa-location-dot" />
          {t('hero.location')}
        </div>
      </div>

      {/* Main content */}
      <div className="hero__content">
        <div className="hero__eyebrow">
          <span className="hero__eyebrow-line" />
          {t('hero.eyebrow')}
        </div>

        <h1 className="hero__title">
          <img
            src={process.env.PUBLIC_URL + '/assets/ckan-logo.png'}
            alt="C-Kan"
            className="hero__title-logo"
            draggable="false"
          />
          <span className="hero__title-sub">
            <em>{safeSubParts[0]}</em> {safeSubParts[1]} <em>{safeSubParts[2]}</em> {safeSubParts[3]}
          </span>
        </h1>

        <p
          className="hero__lede"
          dangerouslySetInnerHTML={{ __html: t('hero.ledeHtml') }}
        />

        <div className="hero__ctas">
          <Link to="/eventos" className="ck-btn">
            <i className="fa-solid fa-ticket" />
            {t('hero.ctaTour')}
          </Link>
          <a
            href="https://open.spotify.com/artist/1QhaqxeqF9sipS2gwbEKpu"
            target="_blank"
            rel="noopener noreferrer"
            className="ck-btn ck-btn--ghost"
          >
            <i className="fa-solid fa-play" />
            {t('hero.ctaListen')}
          </a>
        </div>

        <div className="hero__socials">
          {SOCIALS.map(s => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.label}
              className="hero__social"
            >
              <i className={s.icon} />
            </a>
          ))}
        </div>
      </div>

      {/* Side badge — next show */}
      {nextShow && (
        <aside className="hero__next">
          <span className="hero__next-label">{t('hero.nextLabel')}</span>
          <span className="hero__next-date">{nextDate}</span>
          <span className="hero__next-venue">{nextShow.title}</span>
          <span className="hero__next-city">{nextShow.city}</span>
          {nextShow.ticket_url && (
            <a href={nextShow.ticket_url} target="_blank" rel="noopener noreferrer" className="hero__next-cta">
              {t('hero.nextTickets')} <i className="fa-solid fa-arrow-right" />
            </a>
          )}
        </aside>
      )}

      {/* Bottom stats strip */}
      <div className="hero__stats">
        <div className="hero__stat">
          <span className="hero__stat-num">1B+</span>
          <span className="hero__stat-label">{t('hero.statStreams')}</span>
        </div>
        <div className="hero__stat">
          <span className="hero__stat-num">50+</span>
          <span className="hero__stat-label">{t('hero.statVideos')}</span>
        </div>
        <div className="hero__stat">
          <span className="hero__stat-num">20+</span>
          <span className="hero__stat-label">{t('hero.statYears')}</span>
        </div>
        <div className="hero__stat">
          <span className="hero__stat-num">30+</span>
          <span className="hero__stat-label">{t('hero.statCollabs')}</span>
        </div>
      </div>

      <a href="#evd-section" className="hero__scroll" aria-label={t('hero.scroll')}>
        <span>{t('hero.scroll')}</span>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M6 13l6 6 6-6" />
        </svg>
      </a>
    </section>
  );
}

export default HeroSection;
